import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/base/Breadcrumb';
import EmptyState from '@/components/base/EmptyState';
import MovieCard from '@/components/base/MovieCard';
import { formatDate, searchMovies, searchNews, getTrendingMovies } from '@/lib/catalog';
import { useDocumentMeta } from '@/hooks/useDocumentMeta';

const hotKeywords = ['星际', '悬疑', '时间', '深夜', '武侠', '影评', '雨夜'];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') ?? '';
  const [keyword, setKeyword] = useState(query);
  const trimmed = keyword.trim();

  useDocumentMeta({
    title: trimmed ? `搜索“${trimmed}” - 午夜电影网` : '搜索电影与资讯 - 午夜电影网',
    description: trimmed
      ? `午夜电影网搜索“${trimmed}”的结果，包含匹配的电影与影视资讯，可按结果直接进入影片详情或阅读全文。`
      : '在午夜电影网搜索电影片名、导演、演员或影视资讯关键词，快速找到想看的内容。',
    keywords: '电影搜索,影视搜索,午夜电影网,高清电影',
  });

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  const movies = useMemo(() => searchMovies(keyword), [keyword]);
  const news = useMemo(() => searchNews(keyword), [keyword]);
  const trending = getTrendingMovies(4);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (trimmed) {
      setSearchParams({ q: trimmed });
    } else {
      setSearchParams({});
    }
  };

  const applyKeyword = (value: string) => {
    setKeyword(value);
    setSearchParams({ q: value });
  };

  return (
    <div>
      <section className="relative overflow-hidden border-b border-background-200 bg-accent-100 pb-10 pt-24 md:pb-12 md:pt-32">
        <div className="absolute -right-16 top-8 h-64 w-64 rounded-full bg-primary-500/10 blur-3xl" />
        <div className="shell relative">
          <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索' }]} />
          <h1 className="mt-5 font-heading text-3xl font-semibold tracking-wide text-foreground-950 md:text-4xl">
            {trimmed ? `搜索“${trimmed}”` : '搜索电影与资讯'}
          </h1>
          <p className="mt-2 text-sm text-foreground-500">
            输入片名、导演、演员、类型或资讯关键词，结果会实时更新。
          </p>

          <form onSubmit={submit} className="mt-5 flex w-full flex-col gap-2 sm:flex-row" role="search">
            <div className="flex h-12 min-w-0 flex-1 items-center gap-2 rounded-md border border-background-300/70 bg-background-50 px-4 focus-within:border-primary-500/70">
              <i className="ri-search-line text-base text-foreground-500" aria-hidden="true"></i>
              <input
                type="search"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="例如：星际、悬疑、程砚秋"
                aria-label="搜索电影或资讯"
                className="h-full w-full min-w-0 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword('')}
                  aria-label="清空搜索关键词"
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-foreground-500 hover:text-foreground-900"
                >
                  <i className="ri-close-line" aria-hidden="true"></i>
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex h-12 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-7 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line" aria-hidden="true"></i>
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索：</span>
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => applyKeyword(item)}
                className="whitespace-nowrap rounded-full border border-background-300/70 px-3 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-500/70 hover:text-primary-500"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="shell">
          {!trimmed ? (
            <div className="space-y-10">
              <EmptyState
                icon="ri-search-eye-line"
                title="输入关键词，开始寻找今晚的电影"
                description="可以试试片名、导演、演员，或者直接搜“悬疑”“雨夜”这样的情绪词。"
              />
              <div>
                <h2 className="font-heading text-2xl tracking-wide text-foreground-950">大家都在看</h2>
                <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                  {trending.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-10">
              <div className="flex flex-wrap items-center gap-4 rounded-lg border border-background-200 bg-background-100 px-5 py-4 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5">
                  <i className="ri-film-line text-primary-500" aria-hidden="true"></i>
                  电影 <strong className="font-semibold text-foreground-900">{movies.length}</strong> 部
                </span>
                <span className="h-3 w-px bg-background-400/50" />
                <span className="flex items-center gap-1.5">
                  <i className="ri-newspaper-line text-primary-500" aria-hidden="true"></i>
                  资讯 <strong className="font-semibold text-foreground-900">{news.length}</strong> 条
                </span>
                <span className="h-3 w-px bg-background-400/50" />
                <span>关键词：{trimmed}</span>
              </div>

              {movies.length === 0 && news.length === 0 ? (
                <EmptyState
                  icon="ri-search-line"
                  title={`没有找到与“${trimmed}”相关的内容`}
                  description="换一个更短的关键词试试，或者从热门搜索里挑一个。也可以直接去片库按类型筛选。"
                  actionLabel="浏览电影片库"
                  actionTo="/movies"
                />
              ) : (
                <>
                  {movies.length > 0 && (
                    <div>
                      <div className="flex items-end justify-between">
                        <h2 className="flex items-center gap-2 font-heading text-2xl tracking-wide text-foreground-950">
                          <span className="h-4 w-1 rounded-full bg-primary-500" />
                          电影结果
                        </h2>
                        <Link to="/movies" className="text-[11px] text-foreground-500 transition-colors hover:text-primary-500">
                          去片库继续筛选 →
                        </Link>
                      </div>
                      <div className="mt-5 grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4">
                        {movies.map((movie) => (
                          <MovieCard key={movie.id} movie={movie} showGenres />
                        ))}
                      </div>
                    </div>
                  )}

                  {news.length > 0 && (
                    <div>
                      <h2 className="flex items-center gap-2 font-heading text-2xl tracking-wide text-foreground-950">
                        <span className="h-4 w-1 rounded-full bg-primary-500" />
                        资讯结果
                      </h2>
                      <div className="mt-5 space-y-3">
                        {news.map((item) => (
                          <Link
                            key={item.id}
                            to={`/news/${item.id}`}
                            className="group flex flex-col gap-3 rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-500/40 sm:flex-row sm:items-center"
                          >
                            <span className="h-28 w-full shrink-0 overflow-hidden rounded bg-background-200 sm:h-16 sm:w-28">
                              <img
                                src={item.cover}
                                alt={item.title}
                                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                              />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="flex items-center gap-2 text-[11px] text-foreground-500">
                                <span className="text-primary-500">{item.category}</span>
                                <span className="h-1 w-1 rounded-full bg-foreground-500/60" />
                                <span>{formatDate(item.date)}</span>
                              </span>
                              <span className="mt-1 line-clamp-2 block text-sm leading-6 text-foreground-900 transition-colors group-hover:text-primary-500">
                                {item.title}
                              </span>
                            </span>
                            <i className="ri-arrow-right-line hidden shrink-0 text-foreground-500 transition-transform group-hover:translate-x-1 sm:block" aria-hidden="true"></i>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}